import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BottomNav, TabType } from './components/BottomNav';
import { Dashboard } from './pages/Dashboard';
import { ProductCatalogue } from './pages/ProductCatalogue';
import { ProductDetail } from './pages/ProductDetail';
import { PlanSelection } from './pages/PlanSelection';
import { Wallet } from './pages/Wallet';
import { PolicyHistory } from './pages/PolicyHistory';
import { Claims } from './pages/Claims';
import { Notifications } from './pages/Notifications';
import { AdminPanel } from './pages/AdminPanel';
import { Profile } from './pages/Profile';
import { FundWallet } from './pages/FundWallet';
import { TransferMoney } from './pages/TransferMoney';
import { CardSettings } from './pages/CardSettings';
import { KYC } from './pages/KYC';
import { TransactionDetail } from './pages/TransactionDetail';
const MAGIC_PATTERNS_TEXT = 'Built with Magic Patterns';
function hideMagicPatternsBadges() {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT);
  const toHide: Element[] = [];
  let node: Element | null = walker.nextNode() as Element | null;
  while (node) {
    if (node.childNodes.length > 0) {
      const text = node.textContent?.trim() ?? '';
      if (text.includes(MAGIC_PATTERNS_TEXT)) toHide.push(node);
    }
    node = walker.nextNode() as Element | null;
  }
  toHide.forEach(el => {
    ;
    (el as HTMLElement).style.setProperty('display', 'none', 'important');
  });
}
export function App() {
  const [viewMode, setViewMode] = useState<'app' | 'admin'>('app');
  const [currentScreen, setCurrentScreen] = useState<string>('dashboard');
  const [toggleOpen, setToggleOpen] = useState(false);
  useEffect(() => {
    hideMagicPatternsBadges();
    const observer = new MutationObserver(() => hideMagicPatternsBadges());
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, []);
  // Map screens to bottom nav tabs to keep nav highlighted correctly
  const getActiveTab = (): TabType => {
    if (['dashboard', 'notifications'].includes(currentScreen)) return 'dashboard';
    if (['products', 'product-detail', 'plan-selection'].includes(currentScreen)) return 'products';
    if (['wallet', 'fund-wallet', 'transfer', 'card-settings', 'transaction-detail'].includes(currentScreen)) return 'wallet';
    if (['policies', 'claims'].includes(currentScreen)) return 'policies';
    if (['profile', 'kyc'].includes(currentScreen)) return 'profile';
    return 'dashboard';
  };
  const navigate = (screen: string) => {
    setCurrentScreen(screen);
  };
  const renderScreen = () => {
    switch (currentScreen) {
      case 'dashboard':
        return <Dashboard key="dashboard" navigate={navigate} data-id="element-0" />;
      case 'products':
        return <ProductCatalogue key="products" navigate={navigate} data-id="element-1" />;
      case 'product-detail':
        return <ProductDetail key="product-detail" navigate={navigate} data-id="element-2" />;
      case 'plan-selection':
        return <PlanSelection key="plan-selection" navigate={navigate} data-id="element-3" />;
      case 'wallet':
        return <Wallet key="wallet" navigate={navigate} data-id="element-4" />;
      case 'policies':
        return <PolicyHistory key="policies" navigate={navigate} data-id="element-5" />;
      case 'claims':
        return <Claims key="claims" navigate={navigate} data-id="element-6" />;
      case 'notifications':
        return <Notifications key="notifications" navigate={navigate} data-id="element-7" />;
      case 'profile':
        return <Profile key="profile" navigate={navigate} data-id="element-8" />;
      case 'fund-wallet':
        return <FundWallet key="fund-wallet" navigate={navigate} data-id="element-9" />;
      case 'transfer':
        return <TransferMoney key="transfer" navigate={navigate} data-id="element-10" />;
      case 'card-settings':
        return <CardSettings key="card-settings" navigate={navigate} data-id="element-11" />;
      case 'kyc':
        return <KYC key="kyc" navigate={navigate} data-id="element-12" />;
      case 'transaction-detail':
        return <TransactionDetail key="transaction-detail" navigate={navigate} data-id="element-13" />;
      default:
        return <Dashboard key="dashboard" navigate={navigate} data-id="element-14" />;
    }
  };
  return <div className="min-h-screen bg-gray-100 flex flex-col font-sans relative" data-id="element-15">
      {/* Global Demo Switcher */}
      {/* Desktop: always visible, absolutely positioned */}
      <div className="hidden sm:flex justify-center absolute top-4 left-1/2 -translate-x-1/2 z-[100]" data-id="element-16">
        <div className="bg-white/80 backdrop-blur-md p-1.5 rounded-full shadow-lg border border-gray-200 flex items-center gap-1" data-id="element-17">
          <button onClick={() => setViewMode('app')} className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'app' ? 'bg-[#1B3B36] text-white shadow-md' : 'text-gray-500 hover:text-[#1B3B36] hover:bg-gray-100'}`} data-id="element-18">
            📱 Mobile
          </button>
          <button onClick={() => setViewMode('admin')} className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'admin' ? 'bg-[#1B3B36] text-white shadow-md' : 'text-gray-500 hover:text-[#1B3B36] hover:bg-gray-100'}`} data-id="element-19">
            💻 Admin
          </button>
        </div>
      </div>

      {/* Mobile: collapsible floating toggle */}
      <div className="sm:hidden fixed top-3 left-1/2 -translate-x-1/2 z-[200]" data-id="element-20">
        <AnimatePresence mode="wait" data-id="element-21">
          {toggleOpen ? <motion.div key="expanded" initial={{
          opacity: 0,
          scale: 0.85,
          y: -8
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.85,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="bg-white/90 backdrop-blur-xl p-1 rounded-full shadow-2xl border border-gray-200 flex items-center gap-1" data-id="element-22">
              <button onClick={() => {
            setViewMode('app');
            setToggleOpen(false);
          }} className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${viewMode === 'app' ? 'bg-[#1B3B36] text-white shadow-md' : 'text-gray-500'}`} data-id="element-23">
                Mobile
              </button>
              <button onClick={() => {
            setViewMode('admin');
            setToggleOpen(false);
          }} className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${viewMode === 'admin' ? 'bg-[#1B3B36] text-white shadow-md' : 'text-gray-500'}`} data-id="element-24">
                Admin
              </button>
              <button onClick={() => setToggleOpen(false)} className="ml-0.5 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-25">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-26">
                  <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-27" />
                </svg>
              </button>
            </motion.div> : <motion.button key="collapsed" initial={{
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.85
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} onClick={() => setToggleOpen(true)} className="bg-white/70 backdrop-blur-xl px-3 py-1.5 rounded-full shadow-lg border border-gray-200/80 flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 active:scale-95 transition-transform" data-id="element-28">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1B3B36]" data-id="element-29"></span>
              {viewMode === 'app' ? 'Mobile' : 'Admin'}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="opacity-40" data-id="element-30">
                <path d="M3 4l2 2 2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-31" />
              </svg>
            </motion.button>}
        </AnimatePresence>
      </div>

      {/* Backdrop for mobile toggle */}
      <AnimatePresence data-id="element-32">
        {toggleOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="sm:hidden fixed inset-0 bg-black/10 z-[199]" onClick={() => setToggleOpen(false)} data-id="element-33" />}
      </AnimatePresence>

      {viewMode === 'app' ? <div className="flex-1 flex justify-center items-center sm:py-20" data-id="element-34">
          {/* Mobile Device Container — full height on mobile */}
          <div className="w-full h-[100dvh] sm:h-[850px] max-w-[430px] bg-slate-50 relative sm:rounded-[3rem] sm:shadow-2xl sm:border-[8px] sm:border-gray-900 overflow-hidden flex flex-col" data-id="element-35">
            {/* Main Content Area with AnimatePresence for transitions */}
            <AnimatePresence mode="wait" data-id="element-36">{renderScreen()}</AnimatePresence>

            {/* Bottom Navigation */}
            {!['plan-selection'].includes(currentScreen) && <BottomNav activeTab={getActiveTab()} onChange={tab => navigate(tab)} data-id="element-37" />}
          </div>
        </div> : <div className="flex-1 sm:pt-16" data-id="element-38">
          <AdminPanel data-id="element-39" />
        </div>}
    </div>;
}